import { isNumber } from '@uefadigital/panenka';
import { PkIcon } from '@uefadigital/panenka-react';
import type {
  ICellRendererParams,
  GridApi,
  GridReadyEvent,
  HeaderValueGetterFunc,
  GridOptions,
  CellMouseOverEvent,
  IRowNode,
  ColDef,
} from 'ag-grid-community';
import {
  ModuleRegistry,
  ValidationModule,
  ColumnAutoSizeModule,
  RowAutoHeightModule,
  RowStyleModule,
  CellStyleModule,
  TooltipModule,
  RowSelectionModule,
  GridStateModule,
  ColumnApiModule,
  ClientSideRowModelModule,
  LocaleModule,
  PaginationModule,
} from 'ag-grid-community';
import type { AgGridReactProps } from 'ag-grid-react';
import { AgGridReact } from 'ag-grid-react';
import useResizeObserver from 'beautiful-react-hooks/useResizeObserver';
import clsx from 'clsx';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { useTranslateFn } from '../Translate';

import { omit } from '@utilities/collection-utils';

import * as classes from './styles.module.scss';

import 'ag-grid-community/styles/ag-grid.css';

process.env.NODE_ENV === 'development' && ModuleRegistry.registerModules([ValidationModule]);

ModuleRegistry.registerModules([
  LocaleModule,
  ColumnAutoSizeModule,
  RowAutoHeightModule,
  RowStyleModule,
  CellStyleModule,
  TooltipModule,
  RowSelectionModule,
  GridStateModule,
  PaginationModule,
  ColumnApiModule,
  ClientSideRowModelModule,
]);

export type DataTableProps<TData = any> = AgGridReactProps<TData> & {
  sortable?: boolean;
  movableColumns?: boolean;
  containerClassName?: string;
  sortAscendingIcon?: string;
  sortDescendingIcon?: string;
  onRowHover?: (row: IRowNode<TData>) => void;
};

const DefaultCellRenderer = memo(({ value, column }: ICellRendererParams) => (
  <>{column.getSort() ? <span className='ag-cell--sorted'>{value}</span> : value}</>
));
DefaultCellRenderer.displayName = 'DefaultCellRenderer';

const DataTableGrid = <TData, TValue>(props: DataTableProps<TData>) => {
  const {
    columnDefs,
    defaultColDef,
    columnTypes,
    rowData,
    getRowId,
    sortable = false,
    movableColumns = false,
    sortAscendingIcon = 'navigation-dropdown-up',
    sortDescendingIcon = 'navigation-dropdown-indicator',
    headerHeight,
    rowHeight,
    getRowClass,
    onRowHover,
  } = props;
  const [tableReady, setTableReady] = useState(false);
  const t = useTranslateFn();
  const containerRef = useRef<HTMLDivElement>(null);
  const DOMRect = useResizeObserver(containerRef);

  const gridRef = useRef<AgGridReact<TData>>(null);
  const gridApi = useRef<GridApi<TData>>(null);

  const headerValueGetter: HeaderValueGetterFunc = useCallback(
    ({ colDef }) => {
      const { useShortLabel } = colDef.context ?? {};
      const useAbbr = useShortLabel === true || (isNumber(useShortLabel) && useShortLabel > (DOMRect?.width ?? 0));

      return t(colDef.headerName, { abbr: useAbbr });
    },
    [DOMRect?.width, t],
  );

  const defaultColDefinition = useMemo<ColDef<TData, TValue>>(
    () => ({
      sortable,
      suppressMovable: !movableColumns,
      suppressAutoSize: true,
      headerValueGetter,
      cellRenderer: DefaultCellRenderer,
      autoHeight: true,
      autoHeaderHeight: !isNumber(headerHeight),
      wrapHeaderText: true,
      resizable: false,
      context: {
        useShortLabel: 450,
      },
      ...defaultColDef,
    }),
    [defaultColDef, headerHeight, headerValueGetter, movableColumns, sortable],
  );

  const icons = useMemo<GridOptions['icons']>(
    () => ({
      columnMoveMove: () => renderToStaticMarkup(<PkIcon name='av-fullscreen' size={16} />),
      sortAscending: () => renderToStaticMarkup(<PkIcon name={sortAscendingIcon} size={16} />),
      sortDescending: () => renderToStaticMarkup(<PkIcon name={sortDescendingIcon} size={16} />),
    }),
    [sortAscendingIcon, sortDescendingIcon],
  );

  const onGridReady = useCallback((ev: GridReadyEvent<TData>) => {
    gridApi.current = ev.api;

    setTableReady(true);
  }, []);

  const handleCellMouseOver = (event: CellMouseOverEvent<TData, TValue>) => {
    if (!event.node) return;

    onRowHover?.(event.node);
  };

  useEffect(() => {
    if (!tableReady || !DOMRect?.width || !gridApi.current) return;

    const visibleColumns = (columnDefs as ColDef<TData, TValue>[])
      .filter((col) => col.context?.hiddenUnder && DOMRect.width > col.context?.hiddenUnder)
      .map((c) => c.field);
    gridApi.current.setColumnsVisible(visibleColumns, true);
    const hiddenColumns = (columnDefs as ColDef<TData, TValue>[])
      .filter((col) => col.context?.hiddenUnder && DOMRect.width <= col.context?.hiddenUnder)
      .map((c) => c.field);
    gridApi.current.setColumnsVisible(hiddenColumns, false);
  }, [DOMRect?.width, columnDefs, tableReady]);

  const gridProps = useMemo<AgGridReactProps<TData>>(
    () =>
      omit(
        props,
        'sortable',
        'movableColumns',
        'containerClassName',
        'sortAscendingIcon',
        'sortDescendingIcon',
        'onRowHover',
      ),
    [props],
  );

  return (
    <div ref={containerRef} className={clsx(classes.container, props.containerClassName)}>
      <AgGridReact<TData>
        ref={gridRef}
        theme='legacy'
        domLayout='autoHeight'
        detailRowAutoHeight
        suppressRowHoverHighlight
        suppressCellFocus
        suppressDragLeaveHidesColumns
        suppressColumnVirtualisation
        rowHeight={rowHeight ? rowHeight : 80}
        {...gridProps}
        className={clsx('ag-theme-uefa', props.className)}
        columnDefs={columnDefs}
        columnTypes={columnTypes}
        defaultColDef={defaultColDefinition}
        autoSizeStrategy={{ type: 'fitGridWidth' }}
        icons={icons}
        onGridReady={onGridReady}
        onCellMouseOver={handleCellMouseOver}
        rowData={rowData}
        getRowId={getRowId}
        headerHeight={headerHeight}
        localeText={{ noRowsToShow: '' }}
        getRowClass={getRowClass}
      />
    </div>
  );
};

export default memo(DataTableGrid) as typeof DataTableGrid;
